import { push } from 'connected-react-router';
import moment from 'moment';
import { EventTypes } from 'redux-segment';

import fePickCurrentTime from 'actions/scheduler/fePickCurrentTime.action';
import { aTypeMarkAppointmentEdit } from 'actions/shared.types';
import CONF from 'config/conf.singleton';
import H from 'constants/httpMethod.const';
import { AUTH_API } from 'middleware/api';
import { selectSchedulerAllowCreation } from 'selectors/scheduler.selectors';
import authenticatedEndpoint from 'utils/backend/authenticatedEndpoint.util';
import jsonEndpoint from 'utils/backend/jsonEndpoint.util';
import { fromAnyDateToZuluDayBgn, fromAnyDateToZuluDayEnd } from 'utils/converters/time/from.util';

import { REORDER_STYLISTS } from 'lib/constants/currentSalon';
import {
  CLEAR_NOSHOW_CHARGE_INFO,
  CLEAR_REQUEST_DEPOSIT_SUCCESS,
  DELETE_PREFERENCE_FAILURE,
  DELETE_PREFERENCE_REQUEST,
  DELETE_PREFERENCE_SUCCESS,
  EMPTY_APPOINTMENT_STATUS,
  FETCH_APPOINTMENT_DETAILS_FAILURE,
  FETCH_APPOINTMENT_DETAILS_REQUEST,
  FETCH_APPOINTMENT_DETAILS_SUCCESS,
  FETCH_CLIENT_LABELS_SUCCESS,
  HIDE_INACTIVE_STYLISTS,
  HIDE_INACTIVE_STYLISTS_REQUEST,
  PUT_PROCEDURE_LOG_FAILURE,
  PUT_PROCEDURE_LOG_REQUEST,
  PUT_PROCEDURE_LOG_SUCCESS,
  REQUEST_DEPOSIT_FAILURE,
  REQUEST_DEPOSIT_REQUEST,
  REQUEST_DEPOSIT_SUCCESS,
  SAVE_DAYDATE,
  SAVE_PREFERENCE_REQUEST,
  SAVE_PREFERENCE_SUCCESS,
  SCHEDULER_ALLOW_CREATION,
  SCHEDULER_ALLOW_MODAL,
  SCHEDULER_FETCH_DEPOSIT_FAILURE,
  SCHEDULER_FETCH_DEPOSIT_REQUEST,
  SCHEDULER_FETCH_DEPOSIT_SUCCESS,
  SET_BOOKED_BY_BUTTON,
  SET_CURRENT_STYLIST,
  SET_DRAG_FROM,
  SET_DROP_TIME,
  SET_VIEW,
  SHOW_ALL_STYLISTS,
  STYLISTS_SAVE_ORDER_REQUEST,
  STYLISTS_SAVE_ORDER_SUCCESS,
  UPDATE_NEWBOOKING_DATE,
} from 'scheduler/constants';

export const clearNoshowChargeInfo = () => ({ type: CLEAR_NOSHOW_CHARGE_INFO });
export const clearRequestDepositSuccess = () => ({ type: CLEAR_REQUEST_DEPOSIT_SUCCESS });
export const emptyAppointmentStatuses = () => ({ type: EMPTY_APPOINTMENT_STATUS });
export const setBookedByButton = (tf) => ({ type: SET_BOOKED_BY_BUTTON, tf });
export const setCreation = (allowed) => ({ type: SCHEDULER_ALLOW_CREATION, allowed });
export const setCurrentDay = (currentDay) => ({ type: SAVE_DAYDATE, currentDay });
export const setDragFrom = (stylistId) => ({ type: SET_DRAG_FROM, stylistId });
export const setDropTime = (dropTime) => ({ type: SET_DROP_TIME, dropTime });
export const setModal = (allowed) => ({ type: SCHEDULER_ALLOW_MODAL, allowed });
export const pickCurrentStylist = (stylistId, currentTime) => ({ type: SET_CURRENT_STYLIST, stylistId, currentTime });

export const createIfAllowed = (stylistId, time, url, bookOrTimeoff) => (dispatch, getState) => {
  const state = getState();
  if (!selectSchedulerAllowCreation(state)) return;

  dispatch(pickCurrentStylist(stylistId, time));
  dispatch(fePickCurrentTime({ time, stylistId }));

  if (url.indexOf('new') > 0) dispatch(push(bookOrTimeoff === 'timeoff' ? `${url}/timeoff` : url));
};

export const updateNewBookingDate = (date) => ({ type: UPDATE_NEWBOOKING_DATE, date });
export const reorderStylists = (first, next) => ({ type: REORDER_STYLISTS, first, next });

export const saveStylistOrder = (salonId, stylistIdArray) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/stylist/sort/`,
    authenticated: true,
    types: [STYLISTS_SAVE_ORDER_REQUEST, STYLISTS_SAVE_ORDER_SUCCESS],
    params: { method: 'PUT', stylistIdArray },
    config: { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(stylistIdArray) },
  },
});

export const reorderStylistsThenSave = (first, next) => (dispatch, getState) => {
  dispatch(reorderStylists(first, next)).then(() => {
    const state = getState();
    dispatch(
      saveStylistOrder(
        state.currentSalon.id,
        state.currentSalon.stylistList.map((s) => s.id),
      ),
    );
  });
};

export const hideInactiveStylists = (id, dayDate) => {
  const bgn = fromAnyDateToZuluDayBgn(moment(dayDate).clone());
  const end = fromAnyDateToZuluDayEnd(moment(dayDate).clone());

  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: `salon/${id}/working_stylists/${bgn}/${end}/`,
      authenticated: true,
      types: [HIDE_INACTIVE_STYLISTS_REQUEST, HIDE_INACTIVE_STYLISTS],
      params: { method: 'GET' },
      config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    },
  };
};

export const showAllStylist = (stylistsList) => ({
  type: SHOW_ALL_STYLISTS,
  stylistsList,
  meta: { analytics: EventTypes.track },
});

export const setView = (dayView, selectedDate, stylistId, openingHours) => ({
  type: SET_VIEW,
  dayView,
  selectedDate,
  stylistId,
  openingHours,
  meta: { analytics: EventTypes.track },
});

export const fetchAppointmentDetails = (appointmentId, salonId) => ({
  [AUTH_API]: {
    ...authenticatedEndpoint(),
    ...jsonEndpoint(H.get, `salon/${salonId}/appointment/${appointmentId}/`),
    params: { method: H.get, appointmentId, salonId },
    types: [FETCH_APPOINTMENT_DETAILS_REQUEST, FETCH_APPOINTMENT_DETAILS_SUCCESS, FETCH_APPOINTMENT_DETAILS_FAILURE],
  },
});

export const fetchClientLabels = (clientId, salonId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/appointment/client/${clientId}/labels`,
    authenticated: true,
    types: [FETCH_CLIENT_LABELS_SUCCESS],
    params: { method: 'GET', clientId, salonId },
    config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  },
});

export const fetchDeposit = (salonId, appointmentId) => ({
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `deposit/${salonId}/appointment/${appointmentId}/`,
    authenticated: true,
    types: [SCHEDULER_FETCH_DEPOSIT_REQUEST, SCHEDULER_FETCH_DEPOSIT_SUCCESS, SCHEDULER_FETCH_DEPOSIT_FAILURE],
    params: { method: 'GET', appointmentId, salonId },
  },
});

export const savePreference = (salonId, clientId, stylistId, serviceId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/client/${clientId}/preference/`,
    authenticated: true,
    types: [SAVE_PREFERENCE_REQUEST, SAVE_PREFERENCE_SUCCESS],
    params: { method: 'POST', salonId, clientId },
    config: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stylist: stylistId, service: serviceId }),
    },
    meta: { analytics: EventTypes.track },
  },
});

export const revertProcedureLog = (salonId, procedureId, historyId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/procedures/log/${procedureId}/${historyId}/`,
    authenticated: true,
    types: [PUT_PROCEDURE_LOG_REQUEST, PUT_PROCEDURE_LOG_SUCCESS, PUT_PROCEDURE_LOG_FAILURE],
    params: { method: 'PUT', salonId, procedureId, historyId },
    config: { method: 'PUT', headers: { 'Content-Type': 'application/json' } },
    meta: { analytics: EventTypes.track },
  },
});

export const deletePreference = (salonId, clientId, preferenceId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/client/${clientId}/preference/${preferenceId}/`,
    authenticated: true,
    types: [DELETE_PREFERENCE_REQUEST, DELETE_PREFERENCE_SUCCESS, DELETE_PREFERENCE_FAILURE],
    params: { method: 'DELETE', salonId, clientId, preferenceId },
    config: { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
    meta: { analytics: EventTypes.track },
  },
});

export const markAppointmentEdit = (appointmentId, mark) => ({
  type: aTypeMarkAppointmentEdit.name,
  appointmentId,
  mark,
  meta: { analytics: EventTypes.track },
});

export const requestDeposit = (appointment, requestedDeposit, requestedCardOnly, salonId) => {
  const params = {
    appointment: appointment.id,
    client: appointment.client.id,
    requested_deposit: requestedDeposit || 0,
    requested_card_only: requestedCardOnly,
  };

  const body = JSON.stringify(params);

  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: `salon/${salonId}/invite/client/`,
      authenticated: true,
      types: [REQUEST_DEPOSIT_REQUEST, REQUEST_DEPOSIT_SUCCESS, REQUEST_DEPOSIT_FAILURE],
      params,
      config: { method: 'POST', headers: { 'Content-Type': 'application/json' }, body },
    },
  };
};
